import * as React from 'react';

import { useDndContext, type UniqueIdentifier } from '@dnd-kit/core';
import { SortableContext, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button, cn, Menu } from '@rematter/ui-web';
import { cva } from 'class-variance-authority';
import { useAtomValue } from 'jotai';
import {
  LucideMoreVertical,
  PanelLeftClose,
  PanelLeftOpen,
} from 'lucide-react';

import { $pendingCountByDriver } from '../../state/loadBoard.localState';

import { KanbanTask, TaskCard, type RouteGroupDragData } from './KanbanTask';

type TaskGroup =
  | { type: 'single'; task: KanbanTask }
  | {
      type: 'route';
      routeId: string;
      routeName: string;
      routeStatus: string;
      tasks: KanbanTask[];
    };

function groupTasksByRoute(tasks: KanbanTask[]): TaskGroup[] {
  const sorted = [...tasks].sort((a, b) => {
    if (a.routeId && b.routeId && a.routeId === b.routeId) {
      return (a.routePosition ?? 0) - (b.routePosition ?? 0);
    }
    if (a.routeId && !b.routeId) return -1;
    if (!a.routeId && b.routeId) return 1;
    return 0;
  });

  const groups: TaskGroup[] = [];
  let currentRouteGroup: {
    routeId: string;
    routeName: string;
    routeStatus: string;
    tasks: KanbanTask[];
  } | null = null;

  for (const task of sorted) {
    if (task.routeId) {
      if (currentRouteGroup && currentRouteGroup.routeId === task.routeId) {
        currentRouteGroup.tasks.push(task);
      } else {
        if (currentRouteGroup)
          groups.push({ type: 'route', ...currentRouteGroup });
        currentRouteGroup = {
          routeId: task.routeId,
          routeName: task.routeName ?? 'Route',
          routeStatus: task.routeStatus ?? 'Planned',
          tasks: [task],
        };
      }
    } else {
      if (currentRouteGroup) {
        groups.push({ type: 'route', ...currentRouteGroup });
        currentRouteGroup = null;
      }
      groups.push({ type: 'single', task });
    }
  }
  if (currentRouteGroup) groups.push({ type: 'route', ...currentRouteGroup });
  return groups;
}

type SortableRouteGroupProps = {
  routeId: string;
  routeName: string;
  tasks: KanbanTask[];
  isPending: boolean;
  isOverlay?: boolean;
  onRouteClick?: (routeId: string) => void;
  onTaskClick?: (taskId: UniqueIdentifier) => void;
};

const SortableRouteGroup = ({
  routeId,
  routeName,
  tasks,
  isPending,
  isOverlay,
  onRouteClick,
  onTaskClick,
}: SortableRouteGroupProps) => {
  const sortableId = `route-group-${routeId}`;
  const {
    setNodeRef,
    attributes,
    listeners,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: sortableId,
    data: {
      type: 'RouteGroup',
      routeId,
      routeName,
      tasks,
    } satisfies RouteGroupDragData,
  });

  const style = {
    transition,
    transform: CSS.Translate.toString(transform),
  };

  const innerTaskIds = React.useMemo(() => tasks.map((t) => t.id), [tasks]);

  const handleWrapperClick = (e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest('[data-task-card]')) return;
    onRouteClick?.(routeId);
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        '@SortableRouteGroup',
        '@RouteGroupWrapper flex items-center gap-1 rounded-lg border border-primary/20 bg-primary/5 p-1',
        isOverlay && 'ring-2 ring-primary',
        isDragging && 'opacity-30 ring-2',
      )}
      {...attributes}
      {...listeners}
      onClick={!isOverlay ? handleWrapperClick : undefined}
    >
      <div className="text-[10px] font-semibold text-primary/60 uppercase select-none [writing-mode:vertical-lr]">
        {routeName}
      </div>
      {isPending ? (
        <SortableContext items={innerTaskIds}>
          <div className="flex gap-2">
            {tasks.map((task) => (
              <TaskCard
                key={task.id}
                task={task}
                onClick={
                  !isOverlay
                    ? (e) => {
                        e.stopPropagation();
                        onTaskClick?.(task.id);
                      }
                    : undefined
                }
              />
            ))}
          </div>
        </SortableContext>
      ) : (
        <div className="flex gap-2">
          {tasks.map((task) => (
            <TaskCard
              key={task.id}
              task={task}
              disabled
              onClick={
                !isOverlay
                  ? (e) => {
                      e.stopPropagation();
                      onTaskClick?.(task.id);
                    }
                  : undefined
              }
            />
          ))}
        </div>
      )}
    </div>
  );
};

const VerticalRouteGroup = ({
  routeId,
  routeName,
  tasks,
  isPending,
  onRouteClick,
  onTaskClick,
}: Omit<SortableRouteGroupProps, 'isOverlay'>) => {
  const sortableId = `route-group-${routeId}`;
  const {
    setNodeRef,
    attributes,
    listeners,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: sortableId,
    data: {
      type: 'RouteGroup',
      routeId,
      routeName,
      tasks,
    } satisfies RouteGroupDragData,
  });

  const style = {
    transition,
    transform: CSS.Translate.toString(transform),
  };

  const innerTaskIds = React.useMemo(() => tasks.map((t) => t.id), [tasks]);

  const handleWrapperClick = (e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest('[data-task-card]')) return;
    onRouteClick?.(routeId);
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        '@VerticalRouteGroup flex flex-col gap-2 rounded-lg border border-primary/20 bg-primary/5 p-1.5',
        isDragging && 'opacity-30 ring-2',
      )}
      {...attributes}
      {...listeners}
      onClick={handleWrapperClick}
    >
      <div className="text-[10px] font-semibold text-primary/60 uppercase select-none">
        {routeName}
      </div>
      {isPending ? (
        <SortableContext items={innerTaskIds}>
          <div className="flex flex-col gap-2">
            {tasks.map((task) => (
              <TaskCard
                key={task.id}
                task={task}
                className="w-full"
                onClick={(e) => {
                  e.stopPropagation();
                  onTaskClick?.(task.id);
                }}
              />
            ))}
          </div>
        </SortableContext>
      ) : (
        <div className="flex flex-col gap-2">
          {tasks.map((task) => (
            <TaskCard
              key={task.id}
              task={task}
              disabled
              className="w-full"
              onClick={(e) => {
                e.stopPropagation();
                onTaskClick?.(task.id);
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const kanbanBoardContainerVariants = cva(
  'flex size-full flex-col justify-start gap-2 overflow-auto p-2',
  {
    variants: {
      dragging: {
        default: 'snap-x snap-mandatory',
        active: 'cursor-grabbing snap-none',
      },
    },
  },
);

const kanbanColumnVariants = cva(
  [
    'h-[100px] max-h-[100px] min-w-full',
    'flex snap-center gap-2 overflow-auto',
    'rounded-lg border border-foreground/10 bg-foreground/5',
  ],
  {
    variants: {
      dragging: {
        default: 'border-2 border-transparent',
        over: 'opacity-30 ring-2',
        overlay: 'ring-2 ring-primary',
      },
    },
  },
);

const kanbanColumnHeaderVariants = cva(
  ['w-48 min-w-48 bg-space px-1.5 pt-1.5 pb-1'],
  {
    variants: {
      dragging: {
        over: 'cursor-grabbing',
        overlay: 'cursor-grabbing',
      },
      pinned: {
        true: 'cursor-auto',
        false: 'cursor-grab',
      },
    },
  },
);

const boardColumnContentStyle = cva(['flex w-full overflow-x-auto p-0']);

export interface TKanbanColumn {
  id: UniqueIdentifier;
  title: string;
  header?: () => React.ReactNode;
}

export interface ColumnDragData {
  type: 'Column';
  column: TKanbanColumn;
}

interface BoardColumnProps {
  column: TKanbanColumn;
  tasks: KanbanTask[];
  isOverlay?: boolean;
  onTaskClick?: (taskId: UniqueIdentifier) => void;
  onRouteClick?: (routeId: string) => void;
  onSubmitForDriver?: (driverId: string) => void;
}

export const KanbanColumn = ({
  column,
  tasks,
  onTaskClick,
  onRouteClick,
  onSubmitForDriver,
  isOverlay,
}: BoardColumnProps) => {
  const pendingCountByDriver = useAtomValue($pendingCountByDriver);
  const pendingCount = pendingCountByDriver.get(String(column.id)) ?? 0;
  const groups = React.useMemo(() => groupTasksByRoute(tasks), [tasks]);

  const outerSortableItems = React.useMemo(
    () =>
      groups.map((g) =>
        g.type === 'single' ? g.task.id : `route-group-${g.routeId}`,
      ),
    [groups],
  );

  const {
    setNodeRef,
    attributes,
    listeners,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: column.id,
    data: {
      type: 'Column',
      column,
    } satisfies ColumnDragData,
    attributes: {
      roleDescription: `Column: ${column.title}`,
    },
  });

  const style = {
    transition,
    transform: CSS.Translate.toString(transform),
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        '@KanbanColumn',
        kanbanColumnVariants({
          dragging: isOverlay ? 'overlay' : isDragging ? 'over' : undefined,
        }),
      )}
    >
      <div
        className={cn(
          kanbanColumnHeaderVariants({
            dragging: isOverlay ? 'overlay' : isDragging ? 'over' : undefined,
            pinned: false,
          }),
          'flex items-start justify-between',
        )}
        {...attributes}
        {...listeners}
      >
        {column.header ? (
          <div className="flex min-w-0 flex-1 flex-col self-stretch">
            {column.header()}
          </div>
        ) : (
          <>
            <div className="min-w-0 flex-1 truncate text-sm font-medium">
              {column.title}
            </div>
            {onSubmitForDriver && !isOverlay && (
              <div onPointerDown={(e) => e.stopPropagation()}>
                <Menu
                  trigger={
                    <Button
                      size="icon"
                      variant="outline"
                      icon={<LucideMoreVertical className="size-4" />}
                    />
                  }
                  placement="bottom end"
                  items={[
                    {
                      label:
                        pendingCount > 0
                          ? `Submit ${pendingCount} task${pendingCount > 1 ? 's' : ''} to queue`
                          : 'Submit to driver\u2019s queue',
                      isDisabled: pendingCount === 0,
                      onAction: () => onSubmitForDriver(String(column.id)),
                    },
                  ]}
                />
              </div>
            )}
          </>
        )}
      </div>
      <div className={boardColumnContentStyle()}>
        <div className="flex gap-2 p-1.5">
          <SortableContext items={outerSortableItems}>
            {groups.map((group) =>
              group.type === 'single' ? (
                <TaskCard
                  key={group.task.id}
                  task={group.task}
                  onClick={
                    !isOverlay ? () => onTaskClick?.(group.task.id) : undefined
                  }
                />
              ) : (
                <SortableRouteGroup
                  key={group.routeId}
                  routeId={group.routeId}
                  routeName={group.routeName}
                  tasks={group.tasks}
                  isPending={group.routeStatus === 'Planned'}
                  isOverlay={isOverlay}
                  onRouteClick={onRouteClick}
                  onTaskClick={onTaskClick}
                />
              ),
            )}
          </SortableContext>
        </div>
      </div>
    </div>
  );
};

export const PinnedKanbanColumn = ({
  column,
  tasks,
  onTaskClick,
  onRouteClick,
  onSubmitForDriver,
}: BoardColumnProps) => {
  const pendingCountByDriver = useAtomValue($pendingCountByDriver);
  const pendingCount = pendingCountByDriver.get(String(column.id)) ?? 0;
  const groups = React.useMemo(() => groupTasksByRoute(tasks), [tasks]);

  const outerSortableItems = React.useMemo(
    () =>
      groups.map((g) =>
        g.type === 'single' ? g.task.id : `route-group-${g.routeId}`,
      ),
    [groups],
  );

  const { setNodeRef, attributes } = useSortable({
    id: column.id,
    data: {
      type: 'Column',
      column,
    } satisfies ColumnDragData,
    attributes: {
      roleDescription: `Column: ${column.title}`,
    },
  });

  return (
    <div
      ref={setNodeRef}
      className={cn('@PinnedKanbanColumn', kanbanColumnVariants())}
      {...attributes}
    >
      <div
        className={cn(
          kanbanColumnHeaderVariants({ pinned: true }),
          'flex items-start justify-between',
        )}
      >
        {column.header ? (
          <div className="flex min-w-0 flex-1 flex-col self-stretch">
            {column.header()}
          </div>
        ) : (
          <>
            <div className="min-w-0 flex-1 truncate text-sm font-medium">
              {column.title}
            </div>
            {onSubmitForDriver && (
              <div onPointerDown={(e) => e.stopPropagation()}>
                <Menu
                  trigger={
                    <Button
                      size="icon"
                      variant="outline"
                      icon={<LucideMoreVertical className="size-4" />}
                    />
                  }
                  placement="bottom end"
                  items={[
                    {
                      label:
                        pendingCount > 0
                          ? `Submit ${pendingCount} task${pendingCount > 1 ? 's' : ''} to queue`
                          : 'Submit to driver\u2019s queue',
                      isDisabled: pendingCount === 0,
                      onAction: () => onSubmitForDriver(String(column.id)),
                    },
                  ]}
                />
              </div>
            )}
          </>
        )}
      </div>
      <div className={boardColumnContentStyle()}>
        <div className="flex gap-2 p-1.5">
          <SortableContext items={outerSortableItems}>
            {groups.map((group) =>
              group.type === 'single' ? (
                <TaskCard
                  key={group.task.id}
                  task={group.task}
                  onClick={() => onTaskClick?.(group.task.id)}
                />
              ) : (
                <SortableRouteGroup
                  key={group.routeId}
                  routeId={group.routeId}
                  routeName={group.routeName}
                  tasks={group.tasks}
                  isPending={group.routeStatus === 'Planned'}
                  onRouteClick={onRouteClick}
                  onTaskClick={onTaskClick}
                />
              ),
            )}
          </SortableContext>
        </div>
      </div>
    </div>
  );
};

interface PinnedVerticalColumnProps extends BoardColumnProps {
  emptyMessage?: string;
  collapsed?: boolean;
  onToggle?: () => void;
}

export const PinnedVerticalColumn = ({
  column,
  tasks,
  onTaskClick,
  onRouteClick,
  emptyMessage,
  collapsed,
  onToggle,
}: PinnedVerticalColumnProps) => {
  const groups = React.useMemo(() => groupTasksByRoute(tasks), [tasks]);

  const outerSortableItems = React.useMemo(
    () =>
      groups.map((g) =>
        g.type === 'single' ? g.task.id : `route-group-${g.routeId}`,
      ),
    [groups],
  );

  const { setNodeRef, attributes } = useSortable({
    id: column.id,
    data: {
      type: 'Column',
      column,
    } satisfies ColumnDragData,
    attributes: {
      roleDescription: `Column: ${column.title}`,
    },
  });

  if (collapsed) {
    return (
      <div
        ref={setNodeRef}
        className="@PinnedVerticalColumn flex w-10 min-w-10 flex-col items-center border-r border-foreground/10 bg-foreground/5"
        {...attributes}
      >
        <Button
          variant="ghost"
          size="icon"
          icon={<PanelLeftOpen className="size-4" />}
          onPress={onToggle}
          className="mt-2"
        />
        <div className="mt-2 -rotate-180 text-xs font-medium text-foreground/50 [writing-mode:vertical-lr]">
          {column.title}
          {tasks.length > 0 && ` (${tasks.length})`}
        </div>
      </div>
    );
  }

  return (
    <div
      ref={setNodeRef}
      className="@PinnedVerticalColumn flex w-[280px] min-w-[280px] flex-col border-r border-foreground/10 bg-foreground/5"
      {...attributes}
    >
      <div className="bg-background flex items-center justify-between border-b border-foreground/10 px-3 py-2">
        <div>{column.header?.() ?? column.title}</div>
        <Button
          variant="ghost"
          size="icon"
          icon={<PanelLeftClose className="size-4" />}
          onPress={onToggle}
        />
      </div>
      <div className="flex flex-1 flex-col gap-2 overflow-y-auto p-2">
        {tasks.length === 0 && emptyMessage && (
          <div className="py-8 text-center text-sm text-foreground/40">
            {emptyMessage}
          </div>
        )}
        <SortableContext items={outerSortableItems}>
          {groups.map((group) =>
            group.type === 'single' ? (
              <TaskCard
                key={group.task.id}
                task={group.task}
                className="w-full"
                onClick={() => onTaskClick?.(group.task.id)}
              />
            ) : (
              <VerticalRouteGroup
                key={group.routeId}
                routeId={group.routeId}
                routeName={group.routeName}
                tasks={group.tasks}
                isPending={group.routeStatus === 'Planned'}
                onRouteClick={onRouteClick}
                onTaskClick={onTaskClick}
              />
            ),
          )}
        </SortableContext>
      </div>
    </div>
  );
};

type HaulerSectionDividerProps = { label: string };

export const HaulerSectionDivider = ({ label }: HaulerSectionDividerProps) => (
  <div className="@HaulerSectionDivider flex min-w-full items-center gap-2 px-2 py-1">
    <div className="h-px flex-1 bg-foreground/20" />
    <span className="text-xs font-semibold text-foreground/60 uppercase">
      {label}
    </span>
    <div className="h-px flex-1 bg-foreground/20" />
  </div>
);

type KanbanBoardContainerProps = {
  children: React.ReactNode;
};

export const KanbanBoardContainer = ({
  children,
}: KanbanBoardContainerProps) => {
  const dndContext = useDndContext();

  return (
    <div
      className={cn(
        '@KanbanBoardContainer',
        kanbanBoardContainerVariants({
          dragging: dndContext.active ? 'active' : 'default',
        }),
      )}
    >
      {children}
    </div>
  );
};
